import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { apiZodios } from '~api/apiZodios'
import { type RootState } from '~redux/rootReducer'
import { selectWorkspaceOrgs } from '~redux/slices/organizationSlice'
import { type ContactBook, type IdentityId } from '~types'

import Dropdown, { type DropdownOption } from '../../Dropdown'

export interface ContactBookSelectorProps {
  value?: string
  onSelect?: (book: ContactBook) => void
  searchable?: boolean
  searchThreshold?: number
  placeholder?: string
  disabled?: boolean
  fullWidth?: boolean
  label?: string
}

const ContactBookSelector: React.FC<ContactBookSelectorProps> = ({
  value,
  onSelect,
  searchable,
  searchThreshold = 10,
  placeholder = 'Select contact book',
  disabled = false,
  fullWidth = false,
  label = 'Contact Book',
}) => {
  const user = useSelector((state: RootState) => state?.auth.user)
  const selectedWorkspace = useSelector((state: RootState) => state?.organization.selectedWorkspace)
  const userPermissions = useSelector((state: RootState) => state?.auth.userPermissions)
  const workspaceOrgs = useSelector(selectWorkspaceOrgs)

  const [contactBooks, setContactBooks] = useState<ContactBook[]>([])
  const [selectedId, setSelectedId] = useState<string | undefined>(value)

  useEffect(() => {
    if (!user || !selectedWorkspace || !userPermissions) return

    const contactReadPermissions = userPermissions.permissions?.['contacts:read'] || []
    const workspaceOrgIds = workspaceOrgs.map((org) => org.id)
    const filteredOrgIds = workspaceOrgIds.filter((id) =>
      contactReadPermissions.includes(id as IdentityId),
    )

    if (filteredOrgIds.length === 0) {
      setContactBooks([])
      return
    }

    apiZodios
      .contacts_listPermittedContactBooks({
        user_id: user.id,
        organization_identity_id_list: filteredOrgIds,
        workspace_id: selectedWorkspace.id,
      })
      .then((res) => {
        const books = res.contact_books
        setContactBooks(books)
        const selectedBookExists = books.some((b) => b.id === selectedId)
        if (!selectedBookExists && books.length > 0) {
          setSelectedId(books[0].id)
          onSelect?.(books[0])
        }
      })
      .catch(() => {})
  }, [user?.id, selectedWorkspace?.id, userPermissions, workspaceOrgs])

  useEffect(() => {
    if (value !== undefined && value !== selectedId) {
      setSelectedId(value)
    }
  }, [value])

  const options: DropdownOption[] = useMemo(
    () =>
      contactBooks.map((book) => ({
        value: book.id,
        label: book.name,
      })),
    [contactBooks],
  )

  if (contactBooks.length === 0 || contactBooks.length === 1) return null

  const handleChange = (next: string | string[]): void => {
    const id = Array.isArray(next) ? next[0] : next
    setSelectedId(id)
    const book = contactBooks.find((b) => b.id === id)
    if (book) onSelect?.(book)
  }

  return (
    <Dropdown
      label={label}
      options={options}
      value={selectedId}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      searchable={searchable}
      searchThreshold={searchThreshold}
      fullWidth={fullWidth}
      containerSx={fullWidth ? undefined : { minWidth: 300 }}
    />
  )
}

export default ContactBookSelector
